export type ProfileSignatureEnvelope = {
  ciphertext: string;
  iv: string;
  salt: string;
  algorithm: 'AES-GCM/PBKDF2-SHA256';
  keyHint: 'pin' | 'device';
  updatedAt: string | null;
};

export type ProfileSignatureResponse = {
  hasSignature: boolean;
  signature: ProfileSignatureEnvelope | null;
};

export const PROFILE_SIGNATURE_ALGORITHM = 'AES-GCM/PBKDF2-SHA256' as const;
const PIN_ITERATIONS = 250000;
const LEGACY_PROFILE_SIGNATURE_SESSION_PREFIX = 'profile-signature-session:';
const unlockedProfileSignatures = new Map<string, string>();

export const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.slice(index, index + chunkSize));
  }
  return btoa(binary);
};

export const base64ToBytes = (value: string): Uint8Array => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

export const randomBytes = (length: number): Uint8Array => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
};

export const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
};

const getDeviceKeyStorageKey = (userId: string) => `profile-signature-device-key:${userId}`;

export function getUnlockedProfileSignature(userId: string): string | null {
  return unlockedProfileSignatures.get(userId) ?? null;
}

export function rememberUnlockedProfileSignature(userId: string, signatureBase64: string): void {
  unlockedProfileSignatures.set(userId, signatureBase64);
}

export function forgetUnlockedProfileSignature(userId: string): void {
  unlockedProfileSignatures.delete(userId);
}

export async function clearProfileSignatureSessionCache(): Promise<void> {
  unlockedProfileSignatures.clear();

  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(LEGACY_PROFILE_SIGNATURE_SESSION_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Storage can be unavailable in hardened browser contexts; the in-memory cache is already cleared.
  }
}

export async function getDeviceKey(userId: string): Promise<CryptoKey> {
  const storageKey = getDeviceKeyStorageKey(userId);
  let rawKey = localStorage.getItem(storageKey);
  if (!rawKey) {
    rawKey = bytesToBase64(randomBytes(32));
    localStorage.setItem(storageKey, rawKey);
  }

  return crypto.subtle.importKey(
    'raw',
    toArrayBuffer(base64ToBytes(rawKey)),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function derivePinKey(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(new TextEncoder().encode(pin)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: toArrayBuffer(salt),
      iterations: PIN_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptProfileSignature(params: {
  signatureBase64: string;
  userId: string;
  pin: string;
  allowPinRecovery: boolean;
}): Promise<ProfileSignatureEnvelope> {
  const iv = randomBytes(12);
  const salt = randomBytes(16);
  const key = params.allowPinRecovery
    ? await derivePinKey(params.pin, salt)
    : await getDeviceKey(params.userId);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(new TextEncoder().encode(params.signatureBase64)),
  );

  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    iv: bytesToBase64(iv),
    salt: bytesToBase64(salt),
    algorithm: PROFILE_SIGNATURE_ALGORITHM,
    keyHint: params.allowPinRecovery ? 'pin' : 'device',
    updatedAt: null,
  };
}

export async function decryptProfileSignature(params: {
  envelope: ProfileSignatureEnvelope;
  userId: string;
  pin: string;
}): Promise<string> {
  const salt = base64ToBytes(params.envelope.salt);
  const key = params.envelope.keyHint === 'pin'
    ? await derivePinKey(params.pin, salt)
    : await getDeviceKey(params.userId);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(base64ToBytes(params.envelope.iv)) },
    key,
    toArrayBuffer(base64ToBytes(params.envelope.ciphertext)),
  );
  return new TextDecoder().decode(plaintext);
}
